import { useAuth } from "@/contexts/AuthContext";
import useSWR, { mutate } from "swr";

import { SubscriptionService } from "../services/subscriptionService";
import { useAPI } from "./useAPI";

const SUBSCRIPTION_STATUS_KEY = "/subscription/status";

export const useSubscriptionStatus = () => {
  const v2Api = useAPI("v2");
  const { firmContext } = useAuth();
  const subscriptionService = new SubscriptionService(v2Api);

  const {
    data,
    error,
    isLoading,
    mutate: mutateSubscription,
  } = useSWR<{ hasSubscription: boolean }>(
    firmContext?.firmId ? [SUBSCRIPTION_STATUS_KEY, firmContext.firmId] : null,
    async () => {
      if (!firmContext?.firmId) {
        return { hasSubscription: false };
      }
      const response = await subscriptionService.getActiveSubscription(firmContext.firmId);
      return { hasSubscription: response.has_active_subscription };
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 7 * 24 * 60 * 60 * 1000, // 7 days
    }
  );

  return {
    hasSubscription: data?.hasSubscription ?? false,
    isLoading,
    isError: error,
    mutate: mutateSubscription,
  };
};

/**
 * Invalidates the subscription status cache.
 * Call this after a subscription purchase to refresh the subscription status.
 */
export const invalidateSubscriptionCache = () => {
  mutate((key: unknown) => Array.isArray(key) && key[0] === SUBSCRIPTION_STATUS_KEY, undefined, {
    revalidate: true,
  });
};
